import { defineStore } from 'pinia'
import { ref } from 'vue'

function readTypedFieldValue(entry: unknown): unknown {
  if (entry != null && typeof entry === 'object' && 'value' in (entry as Record<string, unknown>)) {
    return (entry as { value: unknown }).value
  }
  return entry
}

function toBoolean(raw: unknown): boolean {
  if (typeof raw === 'boolean') return raw
  if (typeof raw === 'number') return raw !== 0
  if (typeof raw === 'string') {
    const normalized = raw.trim().toLowerCase()
    return normalized === 'true' || normalized === '1' || normalized === 'yes'
  }
  return Boolean(raw)
}

function toItemId(raw: unknown): string | null {
  if (raw === undefined || raw === null || raw === '') return null
  const n = Number(raw)
  if (Number.isFinite(n) && n === -1) return null
  return String(raw)
}

/**
 * Apply Swing-like ignore table rows (`itemId` + `ignore`) to a Set of menu ids.
 * Pure helper for unit tests.
 */
export function applyIgnoreRowsToSet(
  target: Set<string>,
  rows: unknown,
): Set<string> {
  const next = new Set(target)
  if (!Array.isArray(rows)) return next

  for (const row of rows) {
    if (!row || typeof row !== 'object') continue
    const rowRecord = row as Record<string, unknown>
    const fields =
      rowRecord.fields && typeof rowRecord.fields === 'object'
        ? (rowRecord.fields as Record<string, unknown>)
        : rowRecord
    const itemId = toItemId(readTypedFieldValue(fields.itemId))
    if (!itemId) continue
    const ignore = toBoolean(readTypedFieldValue(fields.ignore))
    if (ignore) next.add(itemId)
    else next.delete(itemId)
  }
  return next
}

export function extractIgnoreTableFromEventData(
  eventData: Record<string, unknown> | null | undefined,
): unknown[] {
  if (!eventData || typeof eventData !== 'object') return []
  let table = readTypedFieldValue(eventData.ignore)
  if (table && typeof table === 'object' && !Array.isArray(table) && 'rows' in (table as object)) {
    table = (table as { rows: unknown }).rows
  }
  return Array.isArray(table) ? table : []
}

export const useMenuNotificationIgnoreStore = defineStore('menuNotificationIgnore', () => {
  const ignoredItemIds = ref<Set<string>>(new Set())

  function isIgnored(itemId: string | number): boolean {
    return ignoredItemIds.value.has(String(itemId))
  }

  function applyIgnoreTable(rows: unknown): void {
    ignoredItemIds.value = applyIgnoreRowsToSet(ignoredItemIds.value, rows)
  }

  function applyFromEventData(eventData: Record<string, unknown> | null | undefined): void {
    applyIgnoreTable(extractIgnoreTableFromEventData(eventData))
  }

  function clear(): void {
    ignoredItemIds.value = new Set()
  }

  return {
    ignoredItemIds,
    isIgnored,
    applyIgnoreTable,
    applyFromEventData,
    clear,
  }
})
